import React from "react";
import styled from "styled-components";
import { PageContainer } from "../../../styles/sharedStyles";
import { ProductTag } from "../../../pages/public/LandingPage/styles";
import { getTypography } from "../../../styles/designTokens";
import { Lightbulb, Zap, Cpu, Rocket, SlidersHorizontal } from "lucide-react"; // lucide-react 아이콘

const SectionContainer = styled(PageContainer)`
  padding: 100px 400px;
  background: linear-gradient(180deg, #fff 0%, #f9fbff 100%);

  /* 대형 태블릿 */
  @media (max-width: 1400px) {
    padding: 80px 200px;
  }

  /* 중형 태블릿 */
  @media (max-width: 1200px) {
    padding: 60px 100px;
  }

  /* 소형 태블릿 */
  @media (max-width: 768px) {
    padding: 50px 50px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    padding: 40px 20px;
  }
`;

const Tag = styled(ProductTag)`
  margin-bottom: 12px;
`;

const Title = styled.h2`
  font-size: 42px;
  font-weight: 700;
  line-height: 1.3;
  color: #111;
  margin-bottom: 60px;

  span {
    background: linear-gradient(90deg, #5c6eff, #7a8cff);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  /* 중형 태블릿 */
  @media (max-width: 1200px) {
    font-size: 36px;
    margin-bottom: 50px;
  }

  /* 소형 태블릿 */
  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 40px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    font-size: 28px;
    margin-bottom: 30px;
  }
`;

const GradientBorder = styled.div`
  padding: 1px;
  background: linear-gradient(90deg, #5c6eff, #9caeff);
  border-radius: 16px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const BenefitsCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px;
  background: #fff;
  border-radius: 16px;
  padding: 48px 60px;

  /* 중형 태블릿 */
  @media (max-width: 1200px) {
    padding: 40px 50px;
    gap: 20px;
  }

  /* 소형 태블릿 */
  @media (max-width: 768px) {
    padding: 32px 30px;
    gap: 24px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    padding: 24px 20px;
    gap: 20px;
  }
`;
const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center; /* ✅ 아이콘을 원 안의 정중앙으로 */
  background: rgba(92, 110, 255, 0.08); /* 은은한 블루 배경 */
  color: #5c6eff;
  margin-bottom: 12px;

  /* 모바일 */
  @media (max-width: 480px) {
    width: 48px;
    height: 48px;
    margin-bottom: 10px;
    
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const BenefitItem = styled.div`
  flex: 1 1 calc(33.333% - 24px);
  min-width: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  transition: all 0.25s ease;
  padding: 16px 8px;

  &:hover {
    transform: translateY(-6px);
  }

  /* 2개씩 배치 */
  @media (max-width: 1000px) {
    flex: 1 1 calc(50% - 20px);
    min-width: 240px;
  }

  /* 소형 태블릿 - 세로 배치 */
  @media (max-width: 768px) {
    flex: 1 1 100%;
    min-width: auto;
    padding: 20px 16px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    padding: 16px 8px;
  }
`;



const BenefitTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #2b2b2b;

  /* 중형 태블릿 */
  @media (max-width: 1200px) {
    font-size: 17px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const BenefitDescription = styled.div`
  ${getTypography("korean", "title2")}
  color: #555;
  line-height: 1.6;

  /* 중형 태블릿 */
  @media (max-width: 1200px) {
    font-size: 14px;
  }

  /* 모바일 */
  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 1.7;
  }
`;

const KeyBenefitsSection: React.FC = () => {
  const benefits = [
    {
      icon: <Lightbulb size={28} />,
      title: "음성을 데이터로, 데이터로 인사이트를",
      description: "문맥과 감정까지 분석해 핵심 정보 도출",
    },
    {
      icon: <Zap size={28} />,
      title: "실시간 인식, 비즈니스의 속도까지 높이다",
      description: "화자별 맞춤 설정으로 빠른 처리",
    },
    {
      icon: <Cpu size={28} />,
      title: "검증된 AI 기술의 신뢰성",
      description: "산업 환경에서도 안정적 결과 제공",
    },
    {
      icon: <Rocket size={28} />,
      title: "도입은 간단히, 확장은 자유롭게",
      description: "필요에 따라 빠른 도입과 유연한 확장",
    },
    {
      icon: <SlidersHorizontal size={28} />,
      title: "산업 맞춤형 AI 최적화",
      description: "비즈니스별로 최적화된 커스터마이징 지원",
    },
  ];  

  return (
    <SectionContainer>
      <Tag>Key Benefits</Tag>
      <Title>
        <span>오디온,</span> 음성 AI의 새로운 기준
      </Title>

      <GradientBorder>
        <BenefitsCard>
          {benefits.map((b, i) => (
            <BenefitItem key={i}>
              <IconWrapper>{b.icon}</IconWrapper>
              <BenefitTitle>{b.title}</BenefitTitle>
              <BenefitDescription>{b.description}</BenefitDescription>
            </BenefitItem>
          ))}
        </BenefitsCard>
      </GradientBorder>
    </SectionContainer>
  );
};

export default KeyBenefitsSection;
